import { type ReactNode, useState } from "react"

import {
  Badge,
  Button,
  NumericInput,
  PermissionTooltip,
  RadioGroup,
  RadioGroupItem,
} from "~/components/design-system/components"
import { Label } from "~/components/reusable/Label"
import { Slider } from "~/components/reusable/Slider"
import { type PermissionRequirement, usePermission } from "~/hooks/usePermission"
import { api } from "~/utils/api"
import { useInvalidateDemandReads } from "~/utils/cacheUtils/demandCacheHelpers"
import {
  clampFramingValue,
  framingAnswerValue,
  FRAMING_QUESTIONS,
  framingOptionDescription,
  isAllSlice,
  sliceKey,
  type FramingSlice,
  framingOptionLabel,
  parseFramingAnswer,
  scopeNode,
  scopeWriteKey,
  valuedAnswer,
  WHOLE_COLLECTION_NODE,
  type AnswerScope,
  type FramingDescribeContext,
  type FramingNode,
  type FramingOptionValue,
  type FramingQuestionId,
} from "~/utils/demandFraming"
import { cn } from "~/utils/helpers"
import { showErrorToast } from "~/utils/toaster"
import { STRATEGY_REQUIRES } from "~/components/demand-2/shared/strategyPermission"

/** The input and optimistic write for a framing question (`utils/demandFraming.ts`). */

export const useSetFramingAnswer = (collectionId: number) => {
  const utils = api.useUtils()
  const invalidateDemandReads = useInvalidateDemandReads("framing_set")
  return api.demand2.setDemandFramingAnswer.useMutation({
    // The radio moving IS the confirmation; the refetch below only reconciles.
    onMutate: async ({ question, segment, groupKey, groupValue, variantId, answer, ...input }) => {
      await utils.demand2.getDemandFraming.cancel({ collectionId })
      utils.demand2.getDemandFraming.setData({ collectionId }, (prev) => {
        const answers = prev?.answers ?? []
        const variantAnswers = prev?.variantAnswers ?? []
        const sliceAnswers = prev?.sliceAnswers ?? []
        const slice: FramingSlice = {
          channel: input.channel ?? "",
          warehouseId: input.warehouseId ?? "",
          marketId: input.marketId ?? "",
        }
        // Slice and variant rows live in their own lists — see `FramingVariantAnswerRow`.
        if (!isAllSlice(slice)) {
          const id = variantId ?? ""
          const rest = sliceAnswers.filter(
            (r) =>
              !(r.question === question && r.variantId === id && sliceKey(r) === sliceKey(slice)),
          )
          return {
            answers,
            variantAnswers,
            sliceAnswers:
              answer == null ? rest : [...rest, { question, variantId: id, ...slice, answer }],
          }
        }
        if (variantId) {
          const rest = variantAnswers.filter(
            (r) => !(r.question === question && r.variantId === variantId),
          )
          return {
            answers,
            variantAnswers: answer == null ? rest : [...rest, { question, variantId, answer }],
            sliceAnswers,
          }
        }
        const node: FramingNode = {
          segment: segment ?? "",
          groupKey: groupKey ?? "",
          groupValue: groupValue ?? "",
        }
        const rest = answers.filter(
          (r) =>
            !(
              r.question === question &&
              r.segment === node.segment &&
              r.groupKey === node.groupKey &&
              r.groupValue === node.groupValue
            ),
        )
        return {
          answers: answer == null ? rest : [...rest, { question, ...node, answer }],
          variantAnswers,
          sliceAnswers,
        }
      })
    },
    onError: (e) => showErrorToast(e),
    // The answer is a demand signal, so every engine read moves with it.
    onSettled: () => invalidateDemandReads(),
  })
}

/** One collection answer for every sales slice, dropping the slices' own answers. */
export const useSetFramingAnswerForAllSlices = () => {
  const invalidateDemandReads = useInvalidateDemandReads("framing_set")
  return api.demand2.setFramingAnswerForAllSlices.useMutation({
    onError: (e) => showErrorToast(e),
    onSettled: () => invalidateDemandReads(),
  })
}

export const useClearVariantFramingAnswers = (collectionId: number) => {
  const utils = api.useUtils()
  const invalidateDemandReads = useInvalidateDemandReads("framing_set")
  return api.demand2.clearVariantFramingAnswers.useMutation({
    onMutate: async ({ question, variantIds }) => {
      await utils.demand2.getDemandFraming.cancel({ collectionId })
      const ids = new Set(variantIds)
      utils.demand2.getDemandFraming.setData({ collectionId }, (prev) =>
        prev
          ? {
              answers: prev.answers,
              variantAnswers: prev.variantAnswers.filter(
                (r) => !(r.question === question && ids.has(r.variantId)),
              ),
              sliceAnswers: prev.sliceAnswers?.filter(
                (r) => !(r.question === question && ids.has(r.variantId)),
              ),
            }
          : prev,
      )
    },
    onError: (e) => showErrorToast(e),
    onSettled: () => invalidateDemandReads(),
  })
}

/**
 * A valued answer's number, typed or (`control: "slider"`) dragged. Commits on Enter, blur or slider
 * release, never per step: every commit is a write. Keyed on the stored answer by the caller, so a
 * landed write resets it.
 */
export const ValueControl = ({
  value,
  pct,
  disabled,
  disabledTitle,
  onCommit,
  badge,
  actions,
  sliderStep = 5,
  sliderMarker,
  footer,
  requires = STRATEGY_REQUIRES,
}: {
  value: FramingOptionValue
  pct: number
  disabled: boolean
  disabledTitle?: string
  onCommit: (pct: number) => void
  badge?: ReactNode
  /** Beside the field, e.g. a reset. */
  actions?: ReactNode
  sliderStep?: number
  /** Drawn over the slider track. */
  sliderMarker?: ReactNode
  footer?: ReactNode
  requires?: PermissionRequirement
}) => {
  // The thumb while it is dragged; null otherwise.
  const [dragging, setDragging] = useState<number | null>(null)
  const min = value.min ?? 0
  const max = value.max ?? 100
  const decimals = value.decimals ?? 0
  const unit = value.unit ?? "%"
  const isSlider = value.control === "slider"
  const commit = (next: number) => {
    setDragging(null)
    const clamped = clampFramingValue(next, value)
    if (clamped !== pct) onCommit(clamped)
  }
  const field = (
    <NumericInput
      allowDecimals={decimals > 0}
      decimals={decimals}
      allowNegative={min < 0}
      allowZero
      min={min}
      max={max}
      unit={unit}
      small
      requires={requires}
      disabled={disabled}
      allowedTitle={disabledTitle}
      containerClassName={cn(isSlider ? "w-24" : "w-28", unit === "units" && "w-36")}
      value={dragging ?? pct}
      onCommit={(next) => next != null && commit(next)}
      aria-label={isSlider ? `${value.label}, typed` : value.label}
    />
  )
  if (!isSlider) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-base bg-subtle px-3 py-2">
        <span className="paragraph-sm text-fg-base">{value.label}</span>
        {field}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2 rounded-md border border-base bg-subtle px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <span className="paragraph-sm text-fg-base flex items-center gap-2">
          {value.label}
          {badge}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {field}
        </div>
      </div>
      <div className="relative">
        <Slider
          min={min}
          max={max}
          step={sliderStep}
          requires={requires}
          disabled={disabled}
          allowedTitle={disabledTitle}
          value={[Math.min(max, Math.max(min, dragging ?? pct))]}
          onValueChange={([next]) => setDragging(next ?? pct)}
          onValueCommit={([next]) => commit(next ?? pct)}
          aria-label={value.label}
        />
        {sliderMarker}
      </div>
      {footer}
    </div>
  )
}

/** Why the pickers lock after a pick: every answer re-runs the demand calculation. */
export const RECALCULATING_TITLE = "Recalculating demand"

/**
 * The answer input for the whole collection, a node or a variant. Writes on change; picking a
 * valued option stores its default at once so the simulation has a number.
 */
export const AnswerPicker = ({
  collectionId,
  question,
  answer,
  node: nodeProp = WHOLE_COLLECTION_NODE,
  scope,
  inherited = false,
  isDefault = false,
  optionDisabled,
  describeContext,
  onWrite,
  pending = false,
  underOption,
  optionFooter,
  followOverall = true,
  title,
  optionCopy,
  className,
  hideTitle = false,
}: {
  collectionId: number
  question: FramingQuestionId
  /** The EFFECTIVE answer: stored, inherited or default. */
  answer: string | undefined
  /** Superseded by `scope` where given; a variant has no node form. */
  node?: FramingNode
  scope?: AnswerScope
  inherited?: boolean
  /**
   * Nothing stored here or wider, so `answer` is the default. The whole collection then shows NO
   * option picked (a default is not a decision); a node still shows it as inherited.
   */
  isDefault?: boolean
  /** Why an option cannot be picked here, or null when it can. */
  optionDisabled?: (optionId: string) => string | null
  describeContext?: FramingDescribeContext
  /** Takes over the write when a pick moves more than this scope's row. */
  onWrite?: (next: string | null) => void
  pending?: boolean
  /** Rendered under the SELECTED option, after its value control. */
  underOption?: (optionId: string) => React.ReactNode
  /** `pct` is null while the option is not selected. */
  optionFooter?: (optionId: string, pct: number | null) => React.ReactNode
  /** Off where no wider scope can be answered yet, so there is nothing to follow. */
  followOverall?: boolean
  /** Replaces the question's title, e.g. to carry a figure or tenant naming the shared file can't. */
  title?: string
  /** Replaces an option's label and description by id, for the same reason. */
  optionCopy?: Partial<Record<string, { label: string; description: string }>>
  className?: string
  hideTitle?: boolean
}) => {
  const { allowed } = usePermission(STRATEGY_REQUIRES)
  const set = useSetFramingAnswer(collectionId)
  const q = FRAMING_QUESTIONS[question]
  const parsed = parseFramingAnswer(answer)
  const recalculating = set.isPending || pending
  const disabled = !allowed || recalculating
  const node = scope ? scopeNode(scope) : nodeProp
  const isVariant = scope?.kind === "variant"
  const isSlice = scope != null && scope.kind !== "node" && !isAllSlice(scope.slice)
  const writeKey = scope ? scopeWriteKey(scope) : { ...node, variantId: "" }
  const write =
    onWrite ??
    ((next: string | null) => set.mutate({ collectionId, question, answer: next, ...writeKey }))
  // Anything narrower than the whole collection can fall back to a wider scope.
  const isNode = node.groupKey !== "" || node.segment !== "" || isVariant || isSlice
  const unanswered = isDefault && !isNode
  const fallbackLabel = unanswered
    ? framingOptionLabel(question, q.defaultOptionId, isNode ? "node" : undefined)
    : null
  const idPrefix = `framing-${question}-${node.segment}-${node.groupKey}-${node.groupValue}-${writeKey.variantId}-${isSlice ? sliceKey(scope.slice!) : ""}`
  return (
    <div className={cn("flex flex-col gap-2 border-t border-base pt-3", className)}>
      {(!hideTitle || (followOverall && isNode)) && (
        <div className="flex items-center justify-between gap-2">
          {!hideTitle && <span className="label-sm text-fg-base">{title ?? q.title}</span>}
          {followOverall && isNode && inherited && (
            <Badge variant="gray" size="xs">
              {node.groupKey !== "" || isVariant || isSlice
                ? "Follows overall"
                : "Follows all variants"}
            </Badge>
          )}
          {followOverall && isNode && !inherited && (
            <Button
              variant="transparent"
              size="xs"
              requires={STRATEGY_REQUIRES}
              onClick={() => write(null)}
            >
              {node.groupKey !== "" || isVariant || isSlice
                ? "Follow overall"
                : "Follow all variants"}
            </Button>
          )}
        </div>
      )}
      {fallbackLabel && (
        <p className="max-w-[50ch] paragraph-sm text-fg-muted">
          The plan follows “{fallbackLabel}” until you answer.
        </p>
      )}
      <RadioGroup
        className="w-full"
        value={unanswered ? "" : (parsed?.optionId ?? "")}
        disabled={disabled}
        onValueChange={(next) => {
          const picked = q.options.find((o) => o.id === next)
          if (!picked?.value) return write(next)
          write(valuedAnswer(next, picked.value.default, picked.value))
        }}
      >
        {q.options
          .filter((o) => isNode || !o.nodeOnly)
          .map((o) => {
            const id = `${idPrefix}-${o.id}`
            const selected = !unanswered && parsed?.optionId === o.id
            const pct = o.value && selected ? framingAnswerValue(question, answer) : null
            const blocked = optionDisabled?.(o.id) ?? null
            return (
              <div key={o.id} className="flex flex-col gap-2">
                <PermissionTooltip
                  requires={STRATEGY_REQUIRES}
                  allowedTitle={recalculating ? RECALCULATING_TITLE : (blocked ?? undefined)}
                  wrapperClassName="w-full"
                >
                  <Label
                    htmlFor={id}
                    className={cn(
                      "flex w-full cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                      selected
                        ? "border-strong bg-highlight"
                        : "border-base bg-base hover:bg-subtle",
                      // Inherited reads as "applies here, but not chosen here".
                      selected && inherited && "border-dashed",
                      blocked != null && "cursor-not-allowed opacity-60",
                    )}
                  >
                    <RadioGroupItem
                      id={id}
                      value={o.id}
                      className="mt-0.5"
                      disabled={blocked != null}
                    />
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="label-sm text-fg-base">
                        {optionCopy?.[o.id]?.label ?? (isNode ? o.nodeLabel : undefined) ?? o.label}
                      </span>
                      <span className="paragraph-sm text-fg-muted">
                        {optionCopy?.[o.id]?.description ??
                          (o.value && pct != null
                            ? o.value.describe(pct, describeContext)
                            : framingOptionDescription(o, describeContext))}
                      </span>
                      {optionFooter?.(o.id, pct)}
                    </span>
                  </Label>
                </PermissionTooltip>
                {o.value && pct != null && (
                  <ValueControl
                    key={answer}
                    value={o.value}
                    pct={pct}
                    disabled={disabled}
                    onCommit={(next) => write(valuedAnswer(o.id, next, o.value))}
                  />
                )}
                {selected && underOption?.(o.id)}
              </div>
            )
          })}
      </RadioGroup>
    </div>
  )
}
